import type { ComponentProps, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useSelectContext } from "./SelectContext";

export type SelectValueProps = Omit<ComponentProps<"span">, "children"> & {
  /** Shown while nothing is selected. */
  placeholder?: ReactNode;
  /**
   * Shown for a selected value whose item has never been rendered — e.g. a pre-filled id before
   * server-side search results include it. Once the item renders, its own label is shown (and
   * remembered), so prefer this over `children` for server-side search. The root's `fallbackLabel`
   * is used when this isn't given.
   */
  fallback?: ReactNode;
  /**
   * Replaces the label while something is selected — the selected item's label in single-select, the
   * "N selected" count in multi-select. The placeholder still shows when nothing is.
   */
  children?: ReactNode;
};

export const SelectValue = ({ placeholder, fallback, children, ...props }: SelectValueProps) => {
  const { t } = useTranslation();
  const { multiple, value, selectedValues, getItem, fallbackLabel } = useSelectContext();
  const singleValue = multiple ? undefined : value || undefined;
  const selectedLabel = singleValue
    ? getItem(singleValue)?.node ?? fallback ?? fallbackLabel?.(singleValue)
    : t("{{amount}} selected", { amount: selectedValues.length });
  const label = selectedValues.length > 0 ? children ?? selectedLabel : undefined;

  return (
    <span data-slot="select-value" {...props}>
      {label ?? placeholder}
    </span>
  );
};
